import { useReducedMotion } from 'framer-motion';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { SPACE_FOLDER_PAGE_SIZE } from 'shared';

import { SpaceBrowserPagination } from '../types/data-room.types';

type Props = {
  // Rows the first page holds, where the list ends once folded; the browser's own by default.
  pageSize?: number;
  pagination: SpaceBrowserPagination;
  // Rows the table shows now; they drop once the folded pages are gone.
  rowCount: number;
  // The table's card: its first-page rows tell where the list will end once folded.
  tableRef: RefObject<HTMLElement | null>;
};

// A smooth scroll reports its end with `scrollend`; this caps the wait where it does not.
const SCROLL_SETTLE_MS = 700;

export const scrollParentOf = (element: HTMLElement | null): HTMLElement | null => {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);

    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }

  return null;
};

export const afterScroll = (container: HTMLElement): Promise<void> =>
  new Promise<void>((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      container.removeEventListener('scrollend', done);
      resolve();
    };
    const timer = window.setTimeout(done, SCROLL_SETTLE_MS);

    container.addEventListener('scrollend', done);
  });

/**
 * "Show less" for a long list. The view first glides up to the last row of the first page, where
 * the list will end; the pages past it then fold away below, out of sight, and the pill rises from
 * under them into its place. Folding where the eye is would pull thousands of pixels of rows past
 * it in a blur. Reduced motion jumps and folds at once.
 */
export const useBrowserPageReveal = ({
  pageSize = SPACE_FOLDER_PAGE_SIZE,
  pagination,
  rowCount,
  tableRef,
}: Props) => {
  const reduceMotion = useReducedMotion();
  const barRef = useRef<HTMLDivElement>(null);
  const isFoldingRef = useRef(false);
  const [isFolding, setIsFolding] = useState(false);

  // The rows are gone once the count drops: the fold is over.
  useEffect(() => {
    if (!isFoldingRef.current) return;

    isFoldingRef.current = false;
    setIsFolding(false);
  }, [rowCount]);

  // Leaves the first page's last row just above where the pill will sit, if the view is below it.
  const scrollToFirstPageEnd = useCallback(async () => {
    const bar = barRef.current;
    const container = scrollParentOf(bar);
    const lastRow = tableRef.current?.querySelector('tbody')?.children[pageSize - 1];

    if (!bar || !container || !lastRow) return;

    const rowBottom =
      lastRow.getBoundingClientRect().bottom -
      container.getBoundingClientRect().top +
      container.scrollTop;
    const paddingBottom = parseFloat(getComputedStyle(container).paddingBottom) || 0;
    const target = Math.max(
      0,
      rowBottom + bar.offsetHeight + paddingBottom - container.clientHeight,
    );

    if (container.scrollTop <= target) return;

    container.scrollTo({ behavior: reduceMotion ? 'auto' : 'smooth', top: target });

    if (!reduceMotion) await afterScroll(container);
  }, [pageSize, reduceMotion, tableRef]);

  const showLess = useCallback(async () => {
    if (isFoldingRef.current) return;

    isFoldingRef.current = true;
    await scrollToFirstPageEnd();

    if (reduceMotion) {
      pagination.showLess();

      return;
    }

    setIsFolding(true);
  }, [pagination, reduceMotion, scrollToFirstPageEnd]);

  // The folding pages report here once they are closed.
  const onPagesFolded = useCallback(() => {
    if (isFoldingRef.current) pagination.showLess();
  }, [pagination]);

  return { barRef, isFolding, onPagesFolded, showLess };
};
